import { useCallback, useEffect, useRef } from "react";
import { canv } from "@/canv/global";
import { useReaction } from "@/interface/FullscreenPresentationLayout/hooks/useReaction";
import { useResize } from "@/interface/FullscreenPresentationLayout/hooks/useResize";
import {
    AVATAR_OFFSET,
    AVATAR_SIZE,
} from "@/interface/FullscreenPresentationLayout/ReactionBar/constants";
import { usePresentation } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/hooks/usePresentation";
import type { PresentationModeData } from "@/interface/RightSheet/Presentations/PresentationsNavigator/types";
import styles from "../FullscreenPresentationLayout.module.scss";

interface PresentationContentProps {
    presentation: PresentationModeData;
    currentSlide: number;
    preview?: string;
    zoom?: number;
    avatar: HTMLImageElement | null;
    reactionSrc: string | null;
    onUserActivity: () => void;
}

export const PresentationContent = ({
    presentation,
    currentSlide,
    zoom = 100,
    avatar,
    reactionSrc,
    onUserActivity,
}: PresentationContentProps) => {
    const presentationData = usePresentation(presentation, currentSlide, false);
    const currentFrame = presentationData?.frames[currentSlide];
    const currentPreview = currentFrame ? presentationData?.previews[currentFrame.UID] : undefined;

    const { canvasRef, containerRef, containerSizeRef, resizeCanvas } = useResize();
    const { reactionRef, setReactionRef, reactionStart } = useReaction();

    const imageRef = useRef<ImageBitmap | null>(null);
    const cursorRef = useRef<{ x: number; y: number } | null>(null);

    const zoomRef = useRef(zoom);
    const dirtyRef = useRef(true);
    const rafRef = useRef<number | null>(null);

    const requestRender = useCallback(() => {
        dirtyRef.current = true;
    }, []);

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            const { left, top } = containerSizeRef.current;
            cursorRef.current = {
                x: e.clientX - left,
                y: e.clientY - top,
            };
            onUserActivity();
            if (avatar) requestRender();
        },
        [avatar, onUserActivity, requestRender, containerSizeRef],
    );

    const handleClick = useCallback(() => {
        if (!reactionSrc || !cursorRef.current) return;
        if (!canvasRef.current) return;

        const { width, height } = canvasRef.current.getBoundingClientRect();

        // конвертируем координаты в нормализованные
        const normalizedX = cursorRef.current.x / width;
        const normalizedY = cursorRef.current.y / height;

        setReactionRef(cursorRef.current.x, cursorRef.current.y, reactionSrc, canvasRef.current);
        requestRender();

        canv.intercom.sendEmoteMessage(normalizedX, normalizedY, reactionSrc, "emotePresentation");
    }, [reactionSrc, setReactionRef, requestRender, canvasRef]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: refs не должны быть в deps
    const draw = useCallback(() => {
        if (!canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const { width, height } = containerSizeRef.current;

        ctx.clearRect(0, 0, width, height);

        const img = imageRef.current;
        if (img) {
            const fitScale = Math.min(width / img.width, height / img.height);
            const finalScale = fitScale * (zoomRef.current / 100);

            const drawWidth = img.width * finalScale;
            const drawHeight = img.height * finalScale;
            const x = (width - drawWidth) / 2;
            const y = (height - drawHeight) / 2;

            if (img.width && img.height) {
                ctx.drawImage(img, x, y, drawWidth, drawHeight);
            }
        }

        if (avatar && cursorRef.current) {
            try {
                const avatarX = cursorRef.current.x + AVATAR_OFFSET;
                const avatarY = cursorRef.current.y + AVATAR_OFFSET;
                ctx.drawImage(avatar, avatarX, avatarY, AVATAR_SIZE, AVATAR_SIZE);
            } catch (e) {
                console.error(e);
            }
        }

        if (reactionRef.current.length) {
            reactionStart();
            dirtyRef.current = true; // пока анимации есть, продолжаем рендер
        }
    }, [avatar, reactionStart]);

    const requestRedraw = useCallback(() => {
        if (dirtyRef.current) {
            dirtyRef.current = false;
            draw();
        }
        rafRef.current = requestAnimationFrame(requestRedraw);
    }, [draw]);

    useEffect(() => {
        rafRef.current = requestAnimationFrame(requestRedraw);
        return () => {
            rafRef.current && cancelAnimationFrame(rafRef.current);
        };
    }, [requestRedraw]);

    useEffect(() => {
        zoomRef.current = zoom;
        requestRender();
    }, [zoom, requestRender]);

    useEffect(() => {
        if (!currentPreview) return;
        imageRef.current = currentPreview;
        requestRender();
    }, [currentPreview, requestRender]);

    useEffect(() => {
        resizeCanvas();
        requestRender();
    }, [resizeCanvas, requestRender]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: containerRef.current не должен быть в deps
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const resizeObserver = new ResizeObserver(() => {
            dirtyRef.current = true;
            draw();
        });

        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, [draw]);

    useEffect(() => {
        const handleRemoteReaction = (e: CustomEvent) => {
            const data = e.detail as { x: number; y: number; url: string };
            if (data?.x !== undefined && data?.y !== undefined && data.url && canvasRef.current) {
                // умножаем на текущий размер canvas
                const { width, height } = canvasRef.current.getBoundingClientRect();
                const x = data.x * width;
                const y = data.y * height;

                setReactionRef(x, y, data.url, canvasRef.current);
                dirtyRef.current = true;
            }
        };

        document.addEventListener(
            "reactionPresentationMessage",
            handleRemoteReaction as EventListener,
        );

        return () => {
            document.removeEventListener(
                "reactionPresentationMessage",
                handleRemoteReaction as EventListener,
            );
        };
    }, [setReactionRef, canvasRef]);

    return (
        <div
            ref={containerRef}
            className={styles.content}
            onMouseMove={handleMouseMove}
            onMouseEnter={onUserActivity}
            onMouseDownCapture={handleClick}
        >
            <div className={styles.contentInner}>
                <canvas ref={canvasRef} />
            </div>
        </div>
    );
};
